import React, { useState, useMemo } from "react";
import locale from "@bonliva-crm/locale";
import { ErrorBoundary } from "@components/error-boundary";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup
} from "@material-ui/core";
import { InfoOutlined } from "@material-ui/icons";
import { PriceListWorkType } from "@bonliva-crm/shared";
import { PrimaryButton, RegularButton, WithTooltip } from "@common";
import {
  defaultWorkTypes,
  getDoctorWorkTypes,
  getPsychologistWorkTypes,
  hideBC_doctorWorkTypes,
  hideBC_psyWorkTypes
} from "@config_editor/ContractsPriceTemplates/utils";

type Props = {
  values: PriceListWorkType[];
  onClose: () => void;
  onSubmit: (v: PriceListWorkType[]) => void;
  open: boolean;
  showPsychologistOpt: boolean;
  showDoctorOpt: boolean;
  isConsultantContract: boolean;
  isBonlivaCare?: boolean;
};

export function WorkTypesAddDialog({
  values,
  onClose,
  open,
  onSubmit,
  showPsychologistOpt,
  showDoctorOpt,
  isConsultantContract,
  isBonlivaCare
}: Props) {
  const [selected, setSelected] = useState(values);
  const docs = useMemo(
    () => (showDoctorOpt ? getDoctorWorkTypes(isConsultantContract) : []),
    [showDoctorOpt, isConsultantContract]
  );
  const psych = useMemo(
    () => (showPsychologistOpt ? getPsychologistWorkTypes(isConsultantContract) : []),
    [showPsychologistOpt, isConsultantContract]
  );

  const options = [...defaultWorkTypes, ...psych, ...docs];
  const getOpt = () => {
    if (isBonlivaCare) {
      let _opt = options;
      if (showPsychologistOpt) {
        _opt = _opt.filter((x) => !hideBC_psyWorkTypes.includes(x.value));
      }
      if (showDoctorOpt) {
        _opt = _opt.filter((x) => !hideBC_doctorWorkTypes.includes(x.value));
      }
      return _opt;
    }
    return options;
  };

  const handleChange = (e) => {
    const { value } = e.target;
    const newVals = selected.includes(value)
      ? selected.filter((x) => x !== value)
      : [...selected, value];
    setSelected(newVals);
  };

  const handleSubmit = () => {
    onSubmit(selected);
    onClose();
  };

  return (
    open && (
      <ErrorBoundary>
        <Dialog open={open} onBackdropClick={onClose}>
          <DialogTitle>{locale.ChooseWorkTypesForContract}</DialogTitle>
          <DialogContent>
            <FormControl>
              <FormGroup>
                {getOpt().map((o) => (
                  <FormControlLabel
                    key={o.value}
                    disabled={values.includes(o.value)}
                    control={
                      <Checkbox
                        checked={selected.includes(o.value)}
                        onChange={handleChange}
                        value={o.value}
                      />
                    }
                    label={
                      <div style={{ display: "flex", alignItems: "center" }}>
                        {o.label}
                        <WithTooltip title={locale.Description}>
                          <InfoOutlined />
                        </WithTooltip>
                      </div>
                    }
                  />
                ))}
              </FormGroup>
            </FormControl>
          </DialogContent>

          <DialogActions>
            <RegularButton onClick={onClose}>{locale.Cancel}</RegularButton>
            <PrimaryButton onClick={handleSubmit}>{locale.Add}</PrimaryButton>
          </DialogActions>
        </Dialog>
      </ErrorBoundary>
    )
  );
}
